import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import clsx from "clsx";
import { memo, useMemo } from "react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { selectedMobileFrame } from "../../../../designSystemIcons";
import { convertPlayerPrice } from "../../../../functions/convertPlayerPrice";
import { extractColors } from "../../../../functions/extractCardColors";
import { getPriceType } from "../../../../functions/getPriceType";
import {
  owned_icon_true as ownedIcon,
  untradeable_icon_true as untradeableIcon,
} from "../../../../icons";
import { getVersionAssetsSelector } from "../../../../redux/loadedContent/loadedContentSlice";
import {
  Tier,
  getRolesPlusOrPlusPlus,
  playerRoleLabelSelector,
  tierSelector,
} from "../../../../redux/players/playersSlice";
import {
  SquadBuilderMode,
  SquadBuilderPlayerType,
  chemStylesToIcon,
} from "../../../../redux/squadBuilder/squadBuilderUtils";
import { SmallButton } from "../../../atoms/Buttons/SmallButton";
import { ClearButton } from "../../../atoms/ClearButton/ClearButton";
import { PlayerSidePanel } from "../../../features/SquadBuilder/components/PlayerSidePanel/PlayerSidePanel";
import { MetaRatingLogoSvg } from "../../../svgs/MetaRatingLogoSvg";
import Chemistry from "../../Chemistry/Chemistry";
import { PlusPlus } from "../../PlusPlus/PlusPlus";
import { PlayStylePlus } from "../PlayStylePlus/PlayStylePlus";
import { Position } from "./Position/Position";

export type SquadBuilderPlayerProps = {
  player: SquadBuilderPlayerType;
  position: string;
  formationPosition?: string;
  metaRating: number;
  metaRatingZeroChem: number;
  chemStyleId: number;
  chemistry?: {
    currentChemistry: number;
    previousChemistry: number;
    isOutOfPosition: boolean;
  };
  playerRoleId: number;
  handleRemovePlayer?: () => void;
  onClickPlayerRole?: () => void;
  onClickPlayer?: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleAddPlayer?: () => void;
  handleChangeChemstyle?: () => void;
  handleAddEvoPlayer?: () => void;
  handleAddPlayerFilters?: () => void;
  isOnPitch?: boolean;
  isOnBench?: boolean;
  shouldScalePlayerRole?: boolean;
  mode?: SquadBuilderMode;
  showPlayerFilters?: boolean;
  tabletOrLargerMode?: boolean;
  desktopMode?: boolean;
  isSelected?: boolean;
  isDragging?: boolean;
  hideMetaRating?: boolean;
  hidePrice?: boolean;
};

type RemovePlayerButtonProps = {
  handleRemovePlayer: () => void;
};
const RemovePlayerButton: React.FC<RemovePlayerButtonProps> = ({
  handleRemovePlayer,
}) => (
  <div className="absolute desktop:right-0 mobile:z-30 touch-device:scale-[117%] -top-3 -right-3 desktop:top-4 p-2 desktop:p-0 z-21">
    <ClearButton onClick={handleRemovePlayer} />
  </div>
);

type PlayerRoleProps = {
  roleLabel: string;
  onClickPlayerRole?: () => void;
  shouldScalePlayerRole?: boolean;
  isOnPitch?: boolean;
  isOnBench?: boolean;
  tabletOrLargerMode?: boolean;
  plus?: boolean;
  plusPlus?: boolean;
};

export const PlayerRole: React.FC<PlayerRoleProps> = ({
  roleLabel,
  onClickPlayerRole,
  shouldScalePlayerRole,
  isOnPitch,
  isOnBench,
  tabletOrLargerMode,
  plus,
  plusPlus,
}) => (
  <div
    className={clsx(
      "absolute bottom-[-8px] inset-x-0 flex items-center justify-center",
      shouldScalePlayerRole && (isOnBench ? "scale-[110%]" : "scale-[117%]"),
      !isOnPitch && !tabletOrLargerMode && "scale-[70%]",
    )}
  >
    {isOnPitch || isOnBench ? (
      <SmallButton
        onClick={() => onClickPlayerRole()}
        disabled={!onClickPlayerRole}
      >
        {roleLabel}
      </SmallButton>
    ) : (
      <PlusPlus plus={plus} plusPlus={plusPlus} withBackground={true} />
    )}
  </div>
);

type CardImageProps = {
  cardUrl: string;
};

const CardImage: React.FC<CardImageProps> = memo(({ cardUrl }) => {
  return <img src={cardUrl} alt={cardUrl} />;
});

type BottomAreaProps = {
  bottomText: string;
  cardName: string;
  position: string;
  attributes: number[];
  isOnPitch?: boolean;
  isOnBench?: boolean;
  tabletOrLargerMode?: boolean;
  assetsBaseUrl: string;
  countryId: number;
  leagueId: number;
  clubId: number;
  isLight: boolean;
};

const BottomArea: React.FC<BottomAreaProps> = memo(
  ({
    bottomText,
    cardName,
    position,
    attributes,
    isOnPitch,
    isOnBench,
    tabletOrLargerMode,
    assetsBaseUrl,
    countryId,
    leagueId,
    clubId,
    isLight,
  }) => {
    const clubLeagueCountry = (
      <div
        className={clsx(
          "w-full mx-auto justify-center gap-x-1 relative sb-v2-md:-mt-0.5",
          !isOnPitch && !tabletOrLargerMode ? "scale-[85%] -mt-1" : "mt-1",
        )}
      >
        <img
          alt={"country"}
          className="absolute sb-v2-md:w-4 w-3 h-[6px] left-[27%] bottom-[-6px] sb-v2-md:bottom-[-10px] sb-v2-md:h-2  sb-v2-md:left-[25%] mt-[2px]"
          src={`${assetsBaseUrl}/fc25/countries/${countryId}.png`}
        />
        <img
          alt={"league"}
          className="absolute w-2 h-2 -translate-x-1/2 left-1/2 sb-v2-md:w-3 sb-v2-md:h-3"
          src={`${assetsBaseUrl}/fc25/leagues/${
            isLight ? "" : "dark/"
          }${leagueId}.png`}
        />
        <img
          alt={"club"}
          className="absolute sb-v2-md:w-3 sb-v2-md:h-3 h-2 w-2 right-[32%] sb-v2-md:right-[30%]"
          src={`${assetsBaseUrl}/fc25/clubs/${
            isLight ? "" : "dark/"
          }${clubId}.png`}
        />
      </div>
    );

    return (
      <div
        className="absolute flex flex-col top-[66%] mobile:top-[69%] mobile:-translate-x-1/2 mobile:left-1/2 w-full mobile:w-auto"
        style={{
          color: bottomText,
        }}
      >
        <div className="relative">
          <div
            className={clsx(
              "w-[calc(120%)] -ml-2 whitespace-nowrap leading-[8px] mobile:leading-[1px] sb-v2-md:text-xs text-center sb-v2-md:leading-[4px] -mt-[2px]",
              isOnPitch ? "text-tiny mt-0.5 mobile:-mt-0.5" : "text-xtiny",
            )}
            style={{
              fontFamily: "CruyffMedium",
            }}
          >
            {cardName}
          </div>
          {(tabletOrLargerMode || (!isOnPitch && !isOnBench)) && (
            <div
              className={clsx(
                "flex flex-row gap-x-[2px] mobile:gap-x-[4px] text-xxtiny sb-v2-md:text-xtiny leading-[8px] sb-v2-md:leading-[10px] -mt-0.5 mobile:mt-1 text-center justify-center items-center",
                !isOnPitch &&
                  !tabletOrLargerMode &&
                  "scale-[80%] touch-device:scale-[90%] leading-[7px] mb-0.5",
              )}
              style={{
                fontFamily: "CruyffCondensedMedium",
              }}
            >
              {attributes.map((attribute, index) => (
                <div key={index} className="flex flex-col">
                  <div>
                    {position === "GK"
                      ? ["DIV", "HAN", "KIC", "REF", "SPE", "POS"][index]
                      : ["PAC", "SHO", "PAS", "DRI", "DEF", "PHY"][index]}
                  </div>
                  <div
                    className="sb-v2-md:text-tiny text-xtiny"
                    style={{ fontFamily: "CruyffMedium" }}
                  >
                    {attribute}
                  </div>
                </div>
              ))}
            </div>
          )}
          {clubLeagueCountry}
        </div>
      </div>
    );
  },
);

const TierLabel = ({ tier }: { tier: Tier }) => (
  <div
    className={clsx(
      "absolute border border-black rounded-sm top-[21%] left-[80%] w-[16px] desktop:w-[18px] touch-device:w-[22px] flex items-center justify-center z-10 text-[8px]  desktop:text-xxs touch-device:text-xs sb-v2-md:text-sm touch-device:pt-[2px] sb-v2-md:pt-[1px] font-medium h-[12px] desktop:h-[16px] touch-device:[h-16px] sb-v2-md:h-[17px] text-black",
      tier?.tierColor,
    )}
  >
    {tier?.tierName}
  </div>
);

type PriceSectionProps = {
  playerPriceFrom: string;
  price: number;
  sbcPrice: number;
  topText: string;
  assetsBaseUrl: string;
  isObjectivePlayer: boolean;
  owned: boolean;
  untradeable: boolean;
};

export const PriceSection: React.FC<PriceSectionProps> = memo(
  ({
    playerPriceFrom,
    price,
    sbcPrice,
    topText,
    assetsBaseUrl,
    isObjectivePlayer,
    owned,
    untradeable,
  }) => (
    <div className="absolute flex w-full mobile:top-2 top-1 sb-v2-md:top-3">
      <div
        className={clsx(
          "flex flex-row gap-x-[2px] px-1 bg-black rounded text-white text-tiny desktop:text-xs sb-v2-md:text-sm border-[1px] items-center w-fit mx-auto",
          (playerPriceFrom !== "market" || price === 0) && "py-[1px]",
        )}
        style={{ borderColor: topText }}
      >
        <img
          className={clsx(
            "my-auto",
            playerPriceFrom === "market" || playerPriceFrom === "xp"
              ? "w-[9px] h-[9px] sb-v2-md:w-[13px] sb-v2-md:h-[13px]"
              : "w-[8px] h-[8px] sb-v2-md:w-[10px] sb-v2-md:h-[10px]",
          )}
          src={
            assetsBaseUrl +
            "/fc25/misc/" +
            (playerPriceFrom === "sbc"
              ? "sbc.png"
              : playerPriceFrom === "xp"
                ? "xp_not_cached.png"
                : "coin.png")
          }
          alt="ut-coin"
        />

        {!isObjectivePlayer && (price > 0 || sbcPrice > 0) && (
          <span className="leading-[9px] sb-v2-md:leading-3 py-[2px]">
            {convertPlayerPrice(price || sbcPrice)}
          </span>
        )}
        {owned && (
          <div className="w-2 h-2 sb-v2-md:w-3 sb-v2-md:h-3 shrink-0">
            {ownedIcon}
          </div>
        )}
        {untradeable && (
          <div className="w-2 h-2 sb-v2-md:w-3 sb-v2-md:h-3 shrink-0">
            {untradeableIcon}
          </div>
        )}
      </div>
    </div>
  ),
);

type PlayerImageProps = {
  playerUrl: string;
  hasDynamicImage: boolean;
  versionId: number;
};

const PlayerImage: React.FC<PlayerImageProps> = memo(
  ({ playerUrl, hasDynamicImage, versionId }) => (
    <img
      alt="player-url"
      className={clsx(
        "absolute flex items-center",
        hasDynamicImage
          ? versionId === 72
            ? "top-[8%] desktop:top-[-4px] right-2 w-16 sb-v2-md:right-0 desktop:w-24 sb-v2-md:w-36"
            : "w-full -top-1 desktop:right-0 desktop:w-28 sb-v2-md:w-36"
          : "w-3/5 desktop:w-[75px] sb-v2-md:w-[84px] desktop:top-[15%] sb-v2-md:top-[16%] top-[20%] right-[8px] mobile:right-[12px] sb-v2-md:right-3",
      )}
      src={playerUrl}
    />
  ),
);

type PlayStylesPlusSsectionProps = {
  playStylesPlus: number[];
  bottomText: string;
  strip: string;
  isMobile: boolean;
};

const PlayStylesPlusSection: React.FC<PlayStylesPlusSsectionProps> = ({
  playStylesPlus,
  bottomText,
  strip,
  isMobile,
}) => (
  <div
    className={clsx(
      "absolute left-0 desktop:left-1 flex flex-col",
      playStylesPlus.length > 2
        ? "md:top-[42%] top-[47%]"
        : playStylesPlus.length > 1
          ? "top-[50%]"
          : "top-[60%]",
    )}
  >
    {playStylesPlus.map((playStylePlusId, index) => (
      <div
        className={clsx(
          "w-3.5 h-3.5 desktop:w-3 desktop:h-3 sb-v2-md:w-4 sb-v2-md:h-4",
          playStylesPlus.length > 4 &&
            index > 0 &&
            (isMobile ? "-mt-1.5" : "-mt-0.75"),
        )}
        key={playStylePlusId}
      >
        <PlayStylePlus
          primaryColor={bottomText}
          secondaryColor={strip}
          id={playStylePlusId}
        />
      </div>
    ))}
  </div>
);

type PositionOnPitchProps = {
  formationPosition?: string;
  plus: boolean;
  plusPlus: boolean;
  tabletOrLargerMode?: boolean;
};

const PositionOnPitch: React.FC<PositionOnPitchProps> = ({
  formationPosition,
  plus,
  plusPlus,
  tabletOrLargerMode,
}) => (
  <div className="absolute bottom-[-22px] mobile:bottom-[-30px] sb-v2-md:bottom-[-32px] z-[-1] flex items-center justify-center inset-x-0">
    <Position tabletOrLargerMode={tabletOrLargerMode}>
      <div className="flex flex-row items-center gap-x-[2px] mobile:gap-x-1">
        {formationPosition}
        <div className={clsx("flex flex-row")}>
          <PlusPlus plus={plus} plusPlus={plusPlus} />
        </div>
      </div>
    </Position>
  </div>
);

type TopLeftAreaProps = {
  actualMetaRating: string | number;
  rating: number;
  position: string;
  chemStyleId: number;
  topText: string;
  strip: string;
  hideMetaRating?: boolean;
};

const TopLeftArea: React.FC<TopLeftAreaProps> = ({
  actualMetaRating,
  rating,
  position,
  chemStyleId,
  topText,
  strip,
  hideMetaRating,
}) => (
  <div
    className="absolute top-[21%] left-[14%] sb-v2-md:top-[21%] sb-v2-md:left-[13%] flex flex-col text-left"
    style={{ color: topText }}
  >
    {!hideMetaRating && actualMetaRating && (
      <div
        className="border-r border-t border-b rounded-r text-tiny desktop:text-xs sb-v2-md:text-sm leading-[8px] desktop:leading-3 sb-v2-md:leading-3 px-[2px] -ml-[14px] touch-device:-ml-4 flex flex-row gap-x-[2px]"
        style={{
          fontFamily: "CruyffCondensedMedium",
          borderColor: topText,
        }}
      >
        <div
          className="px-[2px] -my-[1px] -ml-[5px] rounded-l flex items-center"
          style={{ backgroundColor: topText }}
        >
          <div
            className="w-2 h-2 my-auto sb-v2-md:w-3 sb-v2-md:h-3"
            style={{ color: strip }}
          >
            <MetaRatingLogoSvg frameColor="currentColor" />
          </div>
        </div>
        <div className="pt-[1.5px] touch-device:pt-[2.5px]">
          {actualMetaRating}
        </div>
      </div>
    )}
    <div
      className={cn(
        "text-sm sb-v2-md:text-xl sb-v2-md:-mt-0.5 leading-4 sb-v2-md:leading-7",
        !hideMetaRating && "-ml-0.5",
      )}
      style={{ fontFamily: "CruyffMedium" }}
    >
      {rating}
    </div>
    <div
      className={cn(
        "text-tiny sb-v2-md:text-xs -mt-0.5 sb-v2-md:mt-[-6px]",
        ["CDM", "CAM", "LWB", "RWB"].includes(position)
          ? ""
          : "sb-v2-md:ml-[0px]",
        !hideMetaRating && "-ml-0.5",
      )}
      style={{ fontFamily: "CruyffCondensedMedium" }}
    >
      {position}
    </div>
    <div className="w-fit flex flex-col items-center sb-v2-md:ml-[-1px] -ml-[1px] mt-[-6px]">
      <div
        className="text-xs sb-v2-md:text-sm"
        style={{ fontFamily: "UltimateTeamIcons" }}
      >
        {chemStylesToIcon[chemStyleId]}
      </div>
    </div>
  </div>
);

type ChemistrySectionProps = {
  chemistry: {
    currentChemistry: number;
    previousChemistry: number;
    isOutOfPosition: boolean;
  };
  tabletOrLargerMode?: boolean;
};

const ChemistrySection: React.FC<ChemistrySectionProps> = ({
  chemistry,
  tabletOrLargerMode,
}) => (
  <div className="absolute w-fit -left-0.5 bottom-1 mobile:bottom-2 mobile:left-[2px] sb-v2-md:left-2 z-10">
    <Chemistry
      chemistry={chemistry.currentChemistry as 0 | 1 | 2 | 3}
      previousChemistry={chemistry.previousChemistry as 0 | 1 | 2 | 3}
      isOutOfPosition={chemistry.isOutOfPosition}
      weight={tabletOrLargerMode ? 0.9 : 0.7}
      backgroundColor="bg-gray-900"
    />
  </div>
);

const LoanPlayerIcon: React.FC<{ numberOfGames: number }> = ({
  numberOfGames,
}) => (
  <div className="absolute right-0.5 bottom-1 desktop:bottom-2 w-3.5 h-3.5 desktop:w-5 desktop:h-5 shrink-0 rounded-full bg-primary-blue z-10">
    <div className="relative w-full h-full">
      <div className="text-tiny desktop:text-xs text-white desktop:font-semibold absolute inset-0 text-center">
        {numberOfGames}
      </div>
    </div>
  </div>
);

type PlayerSidePanelWrapperProps = {
  handleRemovePlayer: () => void;
  handleAddPlayer: () => void;
  handleChangeChemstyle: () => void;
  handleAddEvoPlayer: () => void;
  handleAddPlayerFilters: () => void;
  showPlayerFilters: boolean;
  canEvolve: boolean;
  evolutionPath?: number[];
  mode?: SquadBuilderMode;
  isSelected?: boolean;
  isOnPitch?: boolean;
  tabletOrLargerMode?: boolean;
};

const PlayerSidePanelWrapper: React.FC<PlayerSidePanelWrapperProps> = ({
  handleRemovePlayer,
  handleAddPlayer,
  handleChangeChemstyle,
  handleAddEvoPlayer,
  handleAddPlayerFilters,
  showPlayerFilters,
  canEvolve,
  evolutionPath,
  mode,
  isOnPitch,
  tabletOrLargerMode,
}) => {
  return (
    <div className="relative w-full h-full group">
      <div className="absolute inset-0" />
      <div className="absolute desktop:right-0 mobile:z-30 touch-device:scale-[117%] -top-3 -right-3 desktop:top-4 p-2 mobile:p-0 z-21 opacity-0 pointer-events-none transition-opacity duration-300 group-hover:opacity-100 group-hover:pointer-events-auto group-active:opacity-0">
        <ClearButton onClick={handleRemovePlayer} />
      </div>
      {isOnPitch && tabletOrLargerMode && (
        <div className="absolute z-30 -right-[105px] top-12 sb-v2-md:top-16 pointer-events-none opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-hover:pointer-events-auto group-active:opacity-0">
          <PlayerSidePanel
            onClickInsert={handleAddPlayer}
            onClickChemstyle={handleChangeChemstyle}
            onClickEvolve={handleAddEvoPlayer}
            onClickFilters={handleAddPlayerFilters}
            showFilters={showPlayerFilters}
            canEvolve={canEvolve}
            cantChangeChemstyle={
              evolutionPath?.length > 0 || mode === "best-formation"
            }
          />
        </div>
      )}
    </div>
  );
};

export const SquadBuilderPlayer = ({
  player: {
    clubId,
    leagueId,
    countryId,
    rating,
    price,
    owned,
    cardName,
    untradeable,
    versionId,
    metalId,
    attributes,
    playStylesPlus,
    hasDynamicImage,
    playerUrl,
    sbcPrice,
    isObjectivePlayer,
    isSbcPlayer,
    canEvolve,
    evolutionPath,
    isLoanPlayer,
    numberOfLoanedGames,
    rolesPlus,
  },
  playerRoleId,
  metaRating,
  metaRatingZeroChem,
  chemStyleId,
  position,
  formationPosition,
  chemistry,
  handleRemovePlayer,
  onClickPlayerRole,
  onClickPlayer,
  handleAddPlayer,
  handleChangeChemstyle,
  handleAddEvoPlayer,
  handleAddPlayerFilters,
  shouldScalePlayerRole,
  isOnPitch,
  isOnBench,
  mode,
  showPlayerFilters,
  tabletOrLargerMode,
  isSelected,
  desktopMode,
  isDragging,
  hideMetaRating,
  hidePrice,
}: SquadBuilderPlayerProps) => {
  const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;
  const versionAssets = useSelector(getVersionAssetsSelector);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const { topText, bottomText, cardUrl, isLight, strip } = useMemo(() => {
    return extractColors({
      versionAssets,
      versionId,
      metalId,
    });
  }, [versionAssets, versionId, metalId]);

  const isRolesPlusOrPlusPlus = getRolesPlusOrPlusPlus({
    playerRoleId,
    rolesPlus,
  });

  const plus = isRolesPlusOrPlusPlus === "plus";
  const plusPlus = isRolesPlusOrPlusPlus === "plusPlus";

  const actualMetaRating = useMemo(() => {
    return metaRating === 0 || metaRating === undefined
      ? "\u2014"
      : metaRating.toFixed(1);
  }, [metaRating]);

  const tier = useSelector(tierSelector(metaRatingZeroChem, playerRoleId));

  const playerRoleLabel = useSelector(
    playerRoleLabelSelector({
      playerRoleId,
      shortName: isMobile,
      appendPosition: false,
    }),
  );

  const playerPriceFrom = useMemo(() => {
    return getPriceType(price, sbcPrice, isSbcPlayer, isObjectivePlayer);
  }, [price, sbcPrice, isSbcPlayer, isObjectivePlayer]);

  return (
    <div
      className={clsx(
        "relative w-20 h-[110px] mobile:h-[154px] mobile:w-28 sb-v2-md:w-32 sb-v2-md:h-[176px] text-[#412b18]",
        isOnPitch || isOnBench ? "cursor-grab" : "cursor-pointer",
      )}
    >
      {isSelected && !desktopMode && (
        <div className="absolute">
          {selectedMobileFrame}
          <RemovePlayerButton handleRemovePlayer={handleRemovePlayer} />
        </div>
      )}
      <div
        className="absolute w-20 h-[110px] mobile:h-[140px] mobile:w-28 sb-v2-md:w-32 sb-v2-md:h-[162px] z-20"
        onClick={(e) => {
          onClickPlayer && onClickPlayer(e);
        }}
      >
        {(isOnPitch || isOnBench) && tabletOrLargerMode && !isDragging && (
          <PlayerSidePanelWrapper
            {...{
              handleRemovePlayer,
              handleAddPlayer,
              handleChangeChemstyle,
              handleAddEvoPlayer,
              handleAddPlayerFilters,
              showPlayerFilters,
              canEvolve,
              evolutionPath,
              mode,
              isSelected,
              isOnPitch,
              tabletOrLargerMode,
            }}
          />
        )}
      </div>
      <CardImage cardUrl={cardUrl} />
      {(price > 0 || sbcPrice > 0 || isObjectivePlayer || owned) &&
        !hidePrice && (
          <PriceSection
            {...{
              playerPriceFrom,
              price,
              sbcPrice,
              topText,
              assetsBaseUrl,
              isObjectivePlayer,
              owned,
              untradeable,
            }}
          />
        )}
      {tier?.tierName && (!chemistry?.isOutOfPosition || isOnBench) && (
        <TierLabel tier={tier} />
      )}
      <PlayerImage {...{ playerUrl, hasDynamicImage, versionId }} />
      <TopLeftArea
        {...{
          actualMetaRating,
          rating,
          position,
          chemStyleId,
          topText,
          strip,
          hideMetaRating,
        }}
      />
      <BottomArea
        {...{
          bottomText,
          cardName,
          position,
          attributes,
          isOnPitch,
          isOnBench,
          tabletOrLargerMode,
          assetsBaseUrl,
          countryId,
          leagueId,
          clubId,
          isLight,
        }}
      />
      {formationPosition && !isDragging && (
        <PositionOnPitch
          {...{ formationPosition, plus, plusPlus, tabletOrLargerMode }}
        />
      )}
      {chemistry && !isDragging && (
        <ChemistrySection {...{ chemistry, tabletOrLargerMode }} />
      )}
      {isLoanPlayer && <LoanPlayerIcon numberOfGames={numberOfLoanedGames} />}
      {!isDragging && (
        <PlayerRole
          {...{
            roleLabel: playerRoleLabel,
            onClickPlayerRole,
            shouldScalePlayerRole,
            isOnPitch,
            isOnBench,
            tabletOrLargerMode,
            plus,
            plusPlus,
          }}
        />
      )}
      {playStylesPlus?.length > 0 && (
        <PlayStylesPlusSection
          {...{
            playStylesPlus,
            bottomText,
            strip,
            isMobile,
          }}
        />
      )}
    </div>
  );
};
